import DashboardDateRange from '../dashboard-date-range';
import React, {
  ReactNode,
  useState,
  MouseEvent,
  useContext,
  useEffect,
} from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import pathOr from 'ramda/src/pathOr';
import { getUser } from '@service/current-user';
import { DRAWER_ITEMS } from '@components/drawer-header/constants/drawer-items';
import { Icon } from '@components/icon';
import DateSelector from '@components/date-selector';
import DropBoxSelector from '@components/dropbox-selector';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';

import { GlobalContext } from 'context/userContext';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { AxiosInstance } from '@service/axios-instance';
import { logoutUser } from '@service/current-user';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@mui/material/Tooltip';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { FormControl, InputLabel, Select, MenuItem as MuiMenuItem } from '@mui/material';
import { useColorMode } from '../../theme';

import '@components/styles/lara-light-blue/theme.css'
import { updateStoredDateFilter } from './date-filter-storage';
import * as S from './styles';

type Props = {
  children: ReactNode;
  title: string;
  user_info?: boolean;
  hideUI?: boolean;
};

export function DrawerHeader({ children, title, user_info = true, hideUI = false }: Props) {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const [operations, setOperations] = React.useState([]);
  const [setors, setSetors] = React.useState([]);
  const [equipaments, setEquipaments] = React.useState<any[]>([]);

  const {
    mainUser,
    setMainUser,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    operation,
    setOperation,
    setor,
    setSetor,
    equipament,
    setEquipament
  } = useContext(GlobalContext);

  const userIsAdmin = mainUser?.roles?.some?.((role: string) => ['ADMINISTRADOR', 'SUPERADMINISTRADOR', 'Automni-superadmin'].includes(role));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const location = useLocation();
  const headerName = pathOr(title, [location?.pathname], '');
  const navigate = useNavigate();

  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const [tenantTheme, setTenantTheme] = useState(localStorage.getItem('tenant_theme') || 'automni');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [useMaintenanceModule, setUseMaintenanceModule] = useState(false);

  useEffect(() => {
    if (operation) {
      AxiosInstance.get('/operations/current_config', { params: { db: operation }, withCredentials: true })
        .then((res) => {
          if (res.data && res.data.USE_MAINTENANCE_MODULE) {
            setUseMaintenanceModule(true);
          } else {
            setUseMaintenanceModule(false);
          }
        })
        .catch(console.error);
    }
  }, [operation]);

  const userQuery = useQuery({
    queryKey: ['getUser'],
    queryFn: getUser,
    enabled: user_info,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (!user_info) return;

    if (userQuery.isSuccess) {
      const data = userQuery.data?.data;
      setMainUser(data);
    }

    if (userQuery.isError) {
      const err: any = userQuery.error;
      if (err?.response?.status === 401) navigate('/');
    }
  }, [user_info, userQuery.isSuccess, userQuery.isError, userQuery.data, userQuery.error, setMainUser, navigate]);




  const queryClient = useQueryClient();
  const logoutUserMutation = useMutation({
    mutationFn: () => logoutUser(),
    onSuccess: () => {
      queryClient.clear();
      navigate('/');
    },
  });

  const handleUserLogout = () => {
    logoutUserMutation.mutate();
  };

  const logoutButton = (
    <Button
      color="inherit"
      onClick={() => handleUserLogout()}
    >
      <Icon name="logout" alt="settings" />
      <Typography
        variant="h6"
        fontFamily="Mukta"
        color="text.secondary"
        fontWeight={400}
        fontSize={18}
        ml={1}
      >
        Sair
      </Typography>
    </Button>
  );


  useEffect(() => {
    const response = AxiosInstance.get('/general/get_dbs', {
      withCredentials: true,
    })
      .then((res) => {
        setOperations(res.data);
      })
      .catch(e => {

        if (e.response && e.response.status === 401) {
          console.warn("Unauthorized access (401). Redirecting to login or handling it.");
          navigate('/');
        } else {
          console.error("Error fetching DBs:", e);
          console.log("/general/get_dbs error");
        }

      });
    fetchSectorsList();
    fetchEquipamentsList();

  }, [mainUser]);

  function fetchEquipamentsList() {
    if (operation == null) return;

    AxiosInstance.post('/equipaments/get_equipaments?db=' + operation, {}, {
      withCredentials: true,
    })
      .then((response) => {
        const moptions: any[] = [];
        response.data.forEach((eq: any) => {
          const label = eq.apelido ? `${eq.id} - ${eq.apelido}` : String(eq.id);
          moptions.push({ label, value: String(eq.id) });
        })
        console.log(moptions);
        setEquipaments(moptions);
      })
      .catch(e => {

        if (e.response && e.response.status === 401) {
          console.warn("Unauthorized access (401). Redirecting to login or handling it.");
          navigate('/');
        } else {
          console.error("fetch equips error:", e);
          console.log("/fetch equips error");
        }

      });

  }

  function fetchSectorsList() {
    if (operation == null) return;

    const response = AxiosInstance.get('/equipaments/get_sectors?db=' + operation, {
      withCredentials: true,
    })
      .then((response) => {
        let data = response.data || [];
        if (!data.includes("TODOS")) {
          data = ["TODOS", ...data];
        }
        console.log(data);
        setSetors(data);
        if (!setor || setor === 'null' || setor === 'undefined') {
          setSetor("TODOS");
          localStorage.setItem("selectedSetor", "TODOS");
        }

      })
      .catch(e => {

        if (e.response && e.response.status === 401) {
          console.warn("Unauthorized access (401). Redirecting to login or handling it.");
          navigate('/');
        } else {
          console.error("fetch sectors error:", e);
          console.log("/fetch sectors error");
        }

      });

  }

  const drawerWidth = 332;

  if (hideUI) {
    return (
      <S.Content drawerOpen={false} isMobile={isMobile} style={{ marginLeft: 0, marginTop: 0, padding: 0, width: '100vw', height: '100vh', overflow: 'hidden' }}>
        {children}
      </S.Content>
    );
  }

  return (
    <Grid container sx={{ position: 'relative' }}>

      <S.AppBar
        sx={{
          position: 'sticky',
          width: (!isMobile && drawerOpen) ? `calc(100% - ${drawerWidth}px)` : '100%',
          ml: (!isMobile && drawerOpen) ? `${drawerWidth}px` : 0,
          bgcolor: 'background.paper',
          py: 2,
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          overflow: 'visible',
          zIndex: theme.zIndex.appBar,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >

        <Toolbar variant="dense" sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', py: 1, gap: 2 }}>

          <Box display="flex" alignItems="center" gap={1}>
            <IconButton color="inherit" onClick={() => setDrawerOpen(!drawerOpen)}>
              {drawerOpen ? <KeyboardArrowLeftIcon sx={{ color: 'primary.main' }} /> : <MenuIcon sx={{ color: 'primary.main' }} />}
            </IconButton>
            <Typography
              variant="h6"
              fontFamily="Saira"
              color="primary.main"
              fontWeight={600}
              fontSize={26}
            >
              {headerName}
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ ml: 2 }} >
            {['/operation-dashboard', '/equipament-dashboard', '/machine-users-dashboard'].includes(location.pathname) && <DashboardDateRange />}

            {isLocalhost && (
              <FormControl size="small" variant="outlined" sx={{ minWidth: 120 }}>
                <InputLabel id="theme-selector-label">Tema</InputLabel>
                <Select
                  labelId="theme-selector-label"
                  value={tenantTheme}
                  label="Tema"
                  onChange={(e) => {
                    const themeVal = e.target.value as string;
                    localStorage.setItem('tenant_theme', themeVal);
                    setTenantTheme(themeVal);
                    window.location.reload();
                  }}
                >
                  <MuiMenuItem value="automni">Automni</MuiMenuItem>
                  <MuiMenuItem value="greentech">Greentech</MuiMenuItem>
                  <MuiMenuItem value="carsif">Carsif</MuiMenuItem>
                  <MuiMenuItem value="paletrans">Paletrans</MuiMenuItem>
                </Select>
              </FormControl>
            )}

            {(location.pathname === '/operation-dashboard') &&
              (<DropBoxSelector
                label="Setor"
                options={setors}
                value={setor}
                width={320}
                onSelect={(st: any) => {
                  localStorage.setItem("selectedSetor", st);
                  setSetor(st);
                  window.location.reload();
                }}
                error={!setor}
              />)
            }

            {(location.pathname === '/equipament-dashboard') &&
              (<DropBoxSelector
                label="Equipamento"
                options={equipaments}
                value={equipament}
                width={320}
                onSelect={(eq: any) => {
                  localStorage.setItem("selectedEquipament", eq);
                  setEquipament(eq);
                  window.location.reload();
                }}
                error={!equipament}
              />)
            }

            <DropBoxSelector
              label="Operação"
              options={operations}
              value={operation}
              width={320}
              bold={true}
              onSelect={(op: any) => {
                localStorage.setItem("selectedDB", op);
                setOperation(op);
                window.location.reload();
              }}
              error={!operation}
            />




          </Box>
        </Toolbar>

      </S.AppBar>
      <S.Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }} // Better open performance on mobile.
        style={{ display: drawerOpen ? 'block' : 'none' }}
      >
        <S.LogoWrapper>
          <S.LogoIcon />
        </S.LogoWrapper>

        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          ml={4}
          mt={1}
          width="85%"
        >
          <Tooltip
            title={mode === 'dark' ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
            arrow
          >
            <IconButton
              onClick={toggleColorMode}
              aria-label="Alternar modo de tema"
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                border: '1px solid',
                borderColor: mode === 'dark' ? 'rgba(251, 191, 36, 0.4)' : 'divider',
                color: mode === 'dark' ? '#fbbf24' : 'primary.main',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  bgcolor: mode === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(0, 0, 0, 0.08)',
                  transform: 'scale(1.08) rotate(12deg)',
                  boxShadow: mode === 'dark'
                    ? '0 0 14px rgba(251, 191, 36, 0.45)'
                    : '0 2px 8px rgba(0, 0, 0, 0.15)',
                },
              }}
            >
              {mode === 'dark' ? (
                <LightModeOutlinedIcon sx={{ fontSize: 24, filter: 'drop-shadow(0 0 4px #fbbf24)' }} />
              ) : (
                <DarkModeOutlinedIcon sx={{ fontSize: 24 }} />
              )}
            </IconButton>
          </Tooltip>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            ml={1.5}
          >

            <Typography
              variant="subtitle1"
              fontFamily="Saira"
              color="text.primary"
              fontWeight={600}
              fontSize={18}
            >
              {mainUser?.username}
            </Typography>
            <Typography
              variant="subtitle1"
              fontFamily="Saira"
              color="text.secondary"
              fontWeight={400}
              fontSize={13}
            >
              {mainUser?.roles}
            </Typography>
          </Box>
          <Box ml="auto">
            {logoutButton}
          </Box>
        </Box>

        <Divider sx={{ my: 1 }} />
        <List sx={{ mt: 0, p: 0 }}>

          {DRAWER_ITEMS.filter(item => {
            if (item.allowedRoles && mainUser?.roles) {
              const hasRole = item.allowedRoles.some(role => mainUser.roles.includes(role));
              if (!hasRole && !mainUser.roles.includes('Automni-superadmin')) return false;
            }
            if (item.allowedCompanies && mainUser?.employed_by) {
              if (!item.allowedCompanies.includes(mainUser.employed_by)) return false;
            }
            if (item.url === '/maintenance-reports' && !useMaintenanceModule) return false;
            return true;
          }).map((item) => (
            <ListItem key={item.name} sx={{ p: 0 }}>
              <Link
                to={item.url}
                style={{ textDecoration: 'none', color: theme.palette.text.primary, width: '100%' }}
              >
                <ListItemButton>
                  <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                    <Icon width={32} height={32} name={item.icon} alt={item.name} />
                  </ListItemIcon>
                  <S.ListItemText primary={item.name} />
                </ListItemButton>
              </Link>
            </ListItem>
          ))}

        </List>
      </S.Drawer>



      <S.Content drawerOpen={drawerOpen} isMobile={isMobile}>
        {(!operation && location.pathname !== '/operations-setup') ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight="60vh">
            <Typography variant="h4" color="text.secondary" fontFamily="Saira">
              Por favor selecione a operação
            </Typography>
          </Box>
        ) : ((!equipament || equipament === 'null' || equipament === 'undefined' || (equipaments.length > 0 && !equipaments.some((e: any) => e.value === equipament))) && location.pathname === '/equipament-dashboard') ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%" minHeight="60vh">
            <Typography variant="h4" color="text.secondary" fontFamily="Saira">
              Por favor selecione o equipamento
            </Typography>
          </Box>
        ) : (
          children
        )}
      </S.Content>
    </Grid>
  );
}
